import type {
  AppResponseChatSessionResponse,
  ChatSessionResponse,
  ChatSessionUpdate,
} from "@agent-service-types/index.ts";
import type { UseMutationOptions } from "@tanstack/vue-query";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { agentServiceRequest } from "@/services/utils";
import { getChatSessionQueryKey } from "./useGetChatSession";
import { getChatSessionsQueryKey } from "./useGetChatSessions";

interface UpdateChatSessionArgs {
  chatSessionId: string;
  payload: ChatSessionUpdate;
}

export const useUpdateChatSession = (
  useMutationOptions?: UseMutationOptions<ChatSessionResponse, Error, UpdateChatSessionArgs>,
) => {
  const queryClient = useQueryClient();

  return useMutation<ChatSessionResponse, Error, UpdateChatSessionArgs>({
    mutationFn: async ({ chatSessionId, payload }: UpdateChatSessionArgs): Promise<ChatSessionResponse> => {
      const response: AxiosResponse<AppResponseChatSessionResponse> = await agentServiceRequest({
        method: "patch",
        url: `/chat_sessions/${chatSessionId}`,
        payload,
      });
      return response.data.data!;
    },
    onSuccess: async (_data, { chatSessionId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getChatSessionQueryKey(chatSessionId) }),
        queryClient.invalidateQueries({ queryKey: getChatSessionsQueryKey() }),
      ]);
    },
    ...useMutationOptions,
  });
};
